import { For, reactive, watch, type JSX } from 'cdui-js';
import './difi-questionnaire.css';
import { DiFiTextarea } from '../DiFiTextarea';

/* ── 类型定义 ─────────────────────────────────────────────── */

export type QuestionOption = {
  /** 唯一标识（如 'a'、'b'） */
  value: string;
  /** 展示文案 */
  label: string;
};

export type QuestionItem = {
  /** 唯一标识 */
  id: string;
  /** 题目文案（如 "1、您的年龄介乎于哪个组别？"） */
  title: string;
  /** 题型：单选 | 多选 | 文本输入 */
  type: 'radio' | 'checkbox' | 'text';
  /** radio / checkbox 时的选项列表 */
  options?: QuestionOption[];
  /** text 时的 placeholder */
  placeholder?: string;
  /** text 时最多行数，默认 3 */
  rows?: number;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否必填；为 true 时题目标题前显示红色星号 */
  required?: boolean;
};

export type QuestionAnswer = {
  questionId: string;
  /** radio：单个值字符串；checkbox：值数组；text：输入内容 */
  value: string | string[];
  /** 该题是否必填 */
  required: boolean;
  /** 当前是否已填写（必填 + 未填 = 校验不通过） */
  answered: boolean;
};

export type DiFiQuestionnaireProps = {
  /** 题目列表 */
  questions: QuestionItem[];
  /** 每次选择 / 输入后回调，返回当前全部答案快照及 allRequiredAnswered 标志 */
  onChange?: (answers: QuestionAnswer[], allRequiredAnswered: boolean) => void;
  class?: string;
  style?: JSX.CSSProperties;
};

/* ── 内部工具 ─────────────────────────────────────────────── */

function combineClass(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ');
}

function CheckTick() {
  return (
    <svg class="difi-checkbox-tick" viewBox="0 0 10 10" fill="none">
      <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

/* ── 组件 ─────────────────────────────────────────────────── */

export const DiFiQuestionnaire = (props: DiFiQuestionnaireProps) => {
  const answerMap = reactive<Record<string, string | string[]>>({});

  const isAnswered = (q: QuestionItem): boolean => {
    const v = answerMap[q.id];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    return v !== '';
  };

  const collectAll = (): QuestionAnswer[] =>
    props.questions.map((q) => ({
      questionId: q.id,
      value: answerMap[q.id] ?? (q.type === 'checkbox' ? [] : ''),
      required: !!q.required,
      answered: isAnswered(q),
    }));

  const emit = () => {
    const answers = collectAll();
    const allRequiredAnswered = answers.every((a) => !a.required || a.answered);
    props.onChange?.(answers, allRequiredAnswered);
  };

  const selectRadio = (qId: string, optValue: string) => {
    answerMap[qId] = optValue;
    emit();
  };

  const toggleCheckbox = (qId: string, optValue: string) => {
    const prev: string[] = (answerMap[qId] as string[]) || [];
    const idx = prev.indexOf(optValue);
    if (idx >= 0) {
      answerMap[qId] = prev.filter((v) => v !== optValue);
    } else {
      answerMap[qId] = [...prev, optValue];
    }
    emit();
  };

  const updateText = (qId: string, text: string) => {
    answerMap[qId] = text;
    emit();
  };

  const isRadioChecked = (qId: string, optValue: string) => answerMap[qId] === optValue;
  const isCheckboxChecked = (qId: string, optValue: string) => {
    const arr = answerMap[qId];
    return Array.isArray(arr) && arr.includes(optValue);
  };

  const renderRadio = (q: QuestionItem) => (
    <div class="difi-options">
      <For each={q.options ?? []}>
        {(opt) => (
          <div
            class={combineClass('difi-option', q.disabled && 'difi-option--disabled')}
            onclick={() => !q.disabled && selectRadio(q.id, opt.value)}
          >
            <span class={combineClass('difi-radio', isRadioChecked(q.id, opt.value) && 'difi-radio--checked')}>
              {isRadioChecked(q.id, opt.value) ? <span class="difi-radio-dot" /> : null}
            </span>
            <span class="color font-s-S" style="line-height: 20px;">
              {opt.label}
            </span>
          </div>
        )}
      </For>
    </div>
  );

  const renderCheckbox = (q: QuestionItem) => (
    <div class="difi-options">
      <For each={q.options ?? []}>
        {(opt) => (
          <div
            class={combineClass('difi-option', q.disabled && 'difi-option--disabled')}
            onclick={() => !q.disabled && toggleCheckbox(q.id, opt.value)}
          >
            <span class={combineClass('difi-checkbox', isCheckboxChecked(q.id, opt.value) && 'difi-checkbox--checked')}>
              {isCheckboxChecked(q.id, opt.value) ? <CheckTick /> : null}
            </span>
            <span class="color font-s-S" style="line-height: 20px;">
              {opt.label}
            </span>
          </div>
        )}
      </For>
    </div>
  );

  const renderText = (q: QuestionItem) => (
    <DiFiTextarea
      placeholder={q.placeholder ?? '请输入'}
      rows={q.rows ?? 3}
      disabled={q.disabled}
      onUpdateValue={(v) => updateText(q.id, v)}
      onClear={() => updateText(q.id, '')}
    />
  );

  return (
    <div class={combineClass('difi-questionnaire', props.class)} style={props.style}>
      <For each={props.questions}>
        {(q) => (
          <div class="difi-question">
            <p class="difi-question-title color-D font-s">
              {q.required ? <span class="difi-question-required">*</span> : null}
              {q.title}
            </p>
            {q.type === 'radio' ? renderRadio(q) : null}
            {q.type === 'checkbox' ? renderCheckbox(q) : null}
            {q.type === 'text' ? renderText(q) : null}
          </div>
        )}
      </For>
    </div>
  );
};
